import {
  IconCheck,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import {
  MouseEventHandler,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatFolderDetail } from '../../interfaces/chat.interface';
import { ChatContext } from '../../store/chat.store';
import { v4 as uuidv4 } from 'uuid';
import useChatLocalService from '../../services/chat-local.service';
import { ConversationType } from '../../constants/chat.constant';
import useChatApiService from '../../services/chat.service';
import toast from 'react-hot-toast';
import { MessageSquare, MessageSquareLock } from 'lucide-react';
import { useSidebar } from '../ui/sidebar';
import { useQueryClient } from 'react-query';

/**
 * Props for the ConversationComponent
 */
interface Props {
  /** Type of conversation */
  conversationType: ConversationType;
  /** Conversation folder detail data */
  conversation: ChatFolderDetail;
  /** Callback to refetch conversation folders */
  refetchFolders?: () => void;
}

/**
 * ConversationComponent displays a single conversation item in the sidebar with
 * title, selection state, delete functionality, and navigation. Handles conversation
 * selection, deletion, and mobile sidebar toggling.
 * 
 * @component
 * @param {Props} props - Component props
 * @param {ConversationType} props.conversationType - Conversation type
 * @param {ChatFolderDetail} props.conversation - Conversation data
 * @param {() => void} [props.refetchFolders] - Refetch folders callback
 * @returns {JSX.Element} Conversation item with title, actions, and selection state
 * 
 * @example
 * ```tsx
 * <ConversationComponent
 *   conversation={conversation}
 *   conversationType={ConversationType.STANDARD}
 *   refetchFolders={handleRefetch}
 * />
 * ```
 */
export const ConversationComponent = ({ conversation, conversationType, refetchFolders }: Props) => {
  const {
    state: { selectedConversation, conversations, conversationStatusMap },
    dispatch
  } = useContext(ChatContext);
  const [isDeleting, setIsDeleting] = useState(false);
  const { saveSelectedConversation } = useChatLocalService();
  const { deleteChatConversation } = useChatApiService();
  const navigate = useNavigate();
  const { toggleSidebar, isMobile } = useSidebar();
  const queryClient = useQueryClient();

  const updateConversationsAfterDeletion = (conversation: ChatFolderDetail) => {
    // Close sidebar immediately for better UX
    if (isMobile) {
      toggleSidebar();
    }

    const updatedConversations = conversations.filter(
      (c) => c.session_id !== conversation.session_id,
    );
    
    const newStatusMap = new Map(conversationStatusMap);
    newStatusMap.delete(conversation.session_id);

    dispatch({ field: 'conversations', value: updatedConversations });
    dispatch({ field: 'conversationStatusMap', value: newStatusMap });
  }

  const handleDeleteConversation = async (conversation: ChatFolderDetail) => {
    dispatch({ field: 'isLoading', value: true });

    try {
      const response = await deleteChatConversation(conversation.session_id, conversation.conversation_type);

      updateConversationsAfterDeletion(conversation);

      await queryClient.invalidateQueries(["GetChatFolderDetails", conversationType]);

      if (refetchFolders) {
        await refetchFolders();
      }

      if (isMobile) {
        toggleSidebar();
      }

      dispatch({ field: 'isLoading', value: false });
      toast.success(response.message);
  
      const urlType = conversationType.toLowerCase().replace(/_/g, '-');
      navigate(`/chat/${urlType}`, { replace: true });  
      
    } catch (err: any) {
      console.error('Error occurred while deleting user conversation', err);

      dispatch({ field: 'isLoading', value: false });
      toast.error((err && err.error) ? err.error : 'Something went wrong.');
    }
  };

  const handleSelectConversation = (conversation: ChatFolderDetail) => {
    if (selectedConversation?.session_id === conversation.session_id) {
      return;
    }

    dispatch({
      field: 'selectedConversation',
      value: conversation
    });

    const newStatusMap = new Map(conversationStatusMap);
    newStatusMap.set(conversation.session_id, true);
    dispatch({ field: 'conversationStatusMap', value: newStatusMap });

    saveSelectedConversation(conversation.session_id, conversation.conversation_type);
    
    handleNavigationClick();
  };

  const handleNavigationClick = () => {
    const urlType = conversation.conversation_type.toLowerCase().replace(/_/g, '-');
    navigate(`/chat/${urlType}/${conversation.session_id}`);
    if (isMobile) {
      toggleSidebar();
    }
  }

  const handleConfirm: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    if (isDeleting) {
      handleDeleteConversation(conversation)
    }
    setIsDeleting(false);
  };

  const handleCancel: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    setIsDeleting(false);
  };

  const handleOpenDeleteModal: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    setIsDeleting(true);
  };

  return (
    <div className="relative flex items-center">
      <button
        className={`flex w-full cursor-pointer items-center gap-2 p-2
          ${selectedConversation?.session_id === conversation.session_id
            ? 'border border-[#1452B8] rounded-md text-primary-500 bg-primary-500/10'
            : 'border-1 border-[#1452B8]'
          }`}
        draggable="true"
        onClick={() => handleSelectConversation(conversation)}
      >
        {conversation.sensitive ? (
          <MessageSquareLock size={18} />
        ) : (
          <MessageSquare size={18} />
        )}
        <div
          className={`relative max-h-[1.5rem] flex-1 overflow-hidden text-ellipsis whitespace-nowrap break-all text-left text-sm ${selectedConversation?.session_id === conversation.session_id ? 'pr-12' : 'pr-1'
            }`}
        >
          {conversation.title}
        </div>
      </button>

      {(isDeleting) &&
        selectedConversation?.session_id === conversation.session_id && (
          <div className="absolute right-1 z-10 flex">
            <button
              className="min-w-[20px] p-1 hover:text-neutral-500"
              onClick={handleConfirm}
            >
              <IconCheck size={18} />
            </button>
            <button
              className="min-w-[20px] p-1 hover:text-neutral-500"
              onClick={handleCancel}
            >
              <IconX size={18} />
            </button>
          </div>
        )}

      {selectedConversation?.session_id === conversation.session_id &&
        !isDeleting && (
          <div className="absolute right-1 z-10 flex">
            <button
              className="min-w-[20px] p-1  hover:text-neutral-500"
              onClick={handleOpenDeleteModal}
            >
              <IconTrash size={18} />
            </button>
          </div>
        )}
    </div>
  );
};